import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useAuth, apiFetch } from "../../context/AuthContext";

const inputCls =
  "w-full mt-1.5 bg-white/5 border border-white/10 rounded-md px-4 py-3.5 text-base text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors duration-300";

export default function AdminLogin() {
  const { user, checked, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | forgot | forgot-sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (checked && user) {
    const dest = location.state?.from?.pathname || "/admin/applications";
    return <Navigate to={dest} replace />;
  }

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const dest = location.state?.from?.pathname || "/admin/applications";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setMode("forgot-sent");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-background text-foreground flex items-center justify-center px-6"
    >
      <div className="w-full max-w-sm">
        <a href="/" data-testid="admin-login-logo" className="block font-display text-3xl font-bold tracking-tight">
          CAN<span className="text-primary">ARTS</span>
        </a>
        <p className="mt-3 text-xs uppercase tracking-[0.3em] text-secondary">
          Artist Discovery
        </p>
        <h1 className="mt-8 font-display font-bold tracking-tighter text-4xl">
          ADMIN ACCESS
        </h1>

        {mode === "login" && (
          <form onSubmit={submitLogin} className="mt-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Email
              </label>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@canarts.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Password
              </label>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-primary">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-primary text-white px-8 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 transition-[filter] duration-300 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
              <ArrowUpRight size={18} />
            </button>
            <button
              type="button"
              data-testid="admin-forgot-link"
              onClick={() => {
                setMode("forgot");
                setError("");
              }}
              className="w-full text-center text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot} className="mt-8 space-y-5">
            <p className="text-sm font-light text-muted-foreground">
              Enter your admin email and we'll send you a secure reset link.
            </p>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Email
              </label>
              <input
                data-testid="admin-forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="you@canarts.com"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-primary">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-forgot-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-primary text-white px-8 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.15em] hover:brightness-110 transition-[filter] duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send reset link"}
              <ArrowUpRight size={18} />
            </button>
            <button
              type="button"
              data-testid="admin-forgot-back"
              onClick={() => {
                setMode("login");
                setError("");
              }}
              className="w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              <ArrowLeft size={14} /> Back to sign in
            </button>
          </form>
        )}

        {mode === "forgot-sent" && (
          <div className="mt-8 space-y-5" data-testid="admin-forgot-sent">
            <p className="text-sm font-light text-muted-foreground">
              If an admin account exists for <b className="text-foreground">{email}</b>,
              a password reset link is on its way. The link expires in 1 hour.
            </p>
            <button
              data-testid="admin-forgot-back"
              onClick={() => setMode("login")}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              <ArrowLeft size={14} /> Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
